import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LibraryView } from "@/components/LibraryView";
import { CompareView } from "@/components/CompareView";
import { ReportView } from "@/components/ReportView";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useI18n } from "@/i18n";
import { useBranding } from "@/hooks/useBranding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock, ShieldCheck, LogOut, Users, Scale, Loader2, FileDown, Palette } from "lucide-react";
import { Link } from "react-router-dom";
import { downloadReportPdf } from "@/lib/reportPdf";
import { buildCover } from "@/lib/reportCover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "coachscore_admin_pass";

export default function AdminPage() {
  const { t, locale } = useI18n();
  const { branding, logoUrl } = useBranding();
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [tab, setTab] = useState("leads");
  const [comparePair, setComparePair] = useState(null);
  const [viewLead, setViewLead] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [autoPdfFor, setAutoPdfFor] = useState(null);

  const brandName = branding.brand_name || "CoachMeUpp";

  const exportPdf = async (lead) => {
    setPdfLoading(true);
    const id = toast.loading(t("app.preparingPdf"));
    try {
      await downloadReportPdf({
        companyName: lead.company,
        brandName,
        cover: buildCover({
          t,
          locale,
          companyName: lead.company,
          brandName,
          logoUrl,
          accent: branding.brand_color,
          bookingUrl: branding.booking_url,
          modelType: lead.model_type,
          recommendations: lead.recommendations,
        }),
      });
      toast.success(t("app.pdfReady"), { id });
    } catch (e) {
      console.error(e);
      toast.error(t("app.pdfFailed"), { id });
    } finally {
      setPdfLoading(false);
    }
  };

  // Row shortcut: open the lead's report, then export it once it has rendered.
  const quickPdf = (lead) => {
    setViewLead(lead);
    setAutoPdfFor(lead.id);
  };

  useEffect(() => {
    if (!autoPdfFor || !viewLead || viewLead.id !== autoPdfFor) return;
    const timer = setTimeout(() => {
      exportPdf(viewLead);
      setAutoPdfFor(null);
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPdfFor, viewLead]);

  const verify = async (code) => {
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/verify`, { passcode: code });
      localStorage.setItem(STORE_KEY, code);
      setPasscode(code);
      setAuthed(true);
    } catch (e) {
      toast.error(t("admin.invalid"));
      localStorage.removeItem(STORE_KEY);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(STORE_KEY);
    if (saved) verify(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem(STORE_KEY);
    setAuthed(false);
    setPasscode("");
  };

  const handleCompare = (pair) => {
    setComparePair(pair);
    setTab("compare");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="flex justify-center mb-4">
            <LanguageToggle />
          </div>
          <div className="text-center mb-6">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-500/10 mb-4">
              <Lock size={26} className="text-indigo-400" />
            </span>
            <h1 className="font-heading text-2xl font-extrabold">{t("admin.accessTitle")}</h1>
            <p className="text-sm text-slate-500 mt-1">{t("admin.accessDesc")}</p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              verify(passcode);
            }}
            className="space-y-3"
          >
            <Input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder={t("admin.passcodePh")}
              className="bg-[#111827] border-slate-800 text-slate-100 text-center"
              data-testid="admin-passcode-input"
              autoFocus
            />
            <Button
              type="submit"
              disabled={verifying || !passcode}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white"
              data-testid="admin-login-btn"
            >
              {verifying ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" /> {t("admin.verifying")}
                </>
              ) : (
                <>
                  <ShieldCheck size={16} className="mr-2" /> {t("admin.unlock")}
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell min-h-screen bg-[#0B0F17] text-slate-100">
      <header className="no-print sticky top-0 z-40 backdrop-blur-md bg-slate-900/80 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5">
                <img src={logoUrl} alt={branding.brand_name} className="h-8 max-w-[150px] object-contain" />
              </span>
            ) : (
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-lg shadow-indigo-500/20">
                <ShieldCheck size={22} className="text-white" />
              </span>
            )}
            <div>
              <h1 className="font-heading text-lg font-bold leading-none tracking-tight">{t("admin.title")}</h1>
              <p className="text-[11px] text-slate-500 leading-none mt-1">{t("admin.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <Link to="/settings">
              <Button
                variant="outline"
                className="h-9 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                data-testid="admin-settings-link"
              >
                <Palette size={14} className="mr-1.5" /> {t("admin.branding")}
              </Button>
            </Link>
            <Button
              variant="outline"
              className="h-9 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
              onClick={logout}
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} className="mr-1.5" /> {t("admin.lock")}
            </Button>
          </div>
        </div>
      </header>

      <main className="no-print max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800 p-1 h-auto mb-6">
            <TabsTrigger
              value="leads"
              data-testid="admin-tab-leads"
              className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400 gap-1.5 px-4 py-1.5"
            >
              <Users size={15} /> {t("admin.tabLeads")}
            </TabsTrigger>
            <TabsTrigger
              value="compare"
              data-testid="admin-tab-compare"
              className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400 gap-1.5 px-4 py-1.5"
            >
              <Scale size={15} /> {t("admin.tabCompare")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <LibraryView passcode={passcode} onOpen={setViewLead} onCompare={handleCompare} onPdf={quickPdf} />
          </TabsContent>

          <TabsContent value="compare">
            <CompareView pair={comparePair} />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!viewLead} onOpenChange={(o) => !o && setViewLead(null)}>
        <DialogContent className="print-dialog bg-[#0B0F17] border-slate-800 text-slate-100 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-heading text-xl flex items-center justify-between pr-8">
              <span>{t("admin.reportTitle", { company: viewLead?.company || "" })}</span>
              <Button
                size="sm"
                variant="outline"
                className="bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                onClick={() => exportPdf(viewLead)}
                disabled={pdfLoading}
                data-testid="admin-print-btn"
              >
                {pdfLoading ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <FileDown size={14} className="mr-1.5" />
                )}
                {t("admin.print")}
              </Button>
            </DialogTitle>
          </DialogHeader>
          {viewLead && (
            <div className="mt-2">
              <div className="rounded-lg bg-[#111827] border border-slate-800 p-4 mb-4 text-sm no-print">
                <span className="text-slate-400">{t("admin.contact")}</span> {viewLead.name}
                {viewLead.role ? `, ${viewLead.role}` : ""} ·{" "}
                <a href={`mailto:${viewLead.email}`} className="text-indigo-300">
                  {viewLead.email}
                </a>{" "}
                {viewLead.company_size ? `· ${viewLead.company_size} ${t("admin.employees")}` : ""}
              </div>
              <ReportView
                companyName={viewLead.company}
                modelType={viewLead.model_type}
                context={viewLead.context}
                scores={viewLead.scores}
                teamScores={viewLead.team_scores}
                brandName={branding.brand_name}
                logoUrl={logoUrl}
                bookingUrl={branding.booking_url}
                headcounts={viewLead.headcounts}
                recommendations={viewLead.recommendations}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
